import { forwardRef, useCallback, useRef, useState } from 'react';
import { ArrowSmallDownBtn, CloseTab } from '@sas/react-svg';
import cx from 'classnames';
import { setAlpha } from '../../design-tokens';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { BaseButton } from '../Button/__internal__';
import { useDirectSpeech } from '../DirectSpeech';
import { Icon } from '../Icon';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { Flow } from '../Layouts/Flow';
import { BaseMenuButton } from '../MenuButton/__internal__';
import { Text } from '../Text';
import { TokenDesignContainer, useTokenFocusState } from './__internal__';
import { TokenVariant } from './types';
import bundle from './NovaComponents-Token-gui-icu.properties';
import cnBaseButton from '../Button/__internal__/BaseButton.css.json';
import '../Button/__internal__/BaseButton.css';
import cn from './Token.css.json';
import './Token.css';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useTokenDndPrefixId = createUseHtmlId('Token-dndPrefix');
export const TokenDesign = /*#__PURE__*/forwardRef(function TokenDesign(props, ref) {
  const {
    variant,
    testId,
    theme
  } = props;
  const directSpeech = useDirectSpeech();
  const localize = useTextLocalizer();
  const defaultDndPrefix = useTokenDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isPopoverOpen = props.isPopoverOpen || menuIsOpen;
  const textRef = useRef(null);
  const closeRef = useRef(null);
  const {
    onKeyDown,
    onClick,
    onBlur,
    onFocus,
    focusableColumnIndex
  } = useTokenFocusState({
    index: props.index,
    tabIndex: props.tabIndex,
    isPopoverOpen,
    preventScrollOnFocus: props.preventScrollOnFocus,
    textRef,
    closeRef
  });
  const tokenContainerRef = useRef();
  const value = props.value.trim() === '' ? localize('token.missing.label') : props.value;
  let menuSections = [];
  if (props.includeDropdown && !props.disabled) {
    if (props.menuSections && props.menuSections.length > 0) {
      menuSections = props.menuSections;
    } else if (props.menuItems && props.menuItems.length > 0) {
      menuSections = [{
        items: props.menuItems
      }];
    }
  }
  const setTokenContainerRef = useCallback(tokenContainer => {
    if (tokenContainer) {
      tokenContainerRef.current = tokenContainer;
      setRef(ref, tokenContainer);
    }
  }, [ref]);
  const dt = useDesignTokens();
  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const getIconA11yInfo = useIconA11yInfo();
  const iconA11yInfo = props.icon ? getIconA11yInfo(props.icon) : undefined;
  const tokens = {
    ...getTokenDesignTokens(dt, {
      variant,
      disabled: props.disabled,
      isNewTokenValueFlagEnabled
    }),
    ...theme?.(dt)
  };
  const iconAriaLabel = iconA11yInfo?.ariaLabel ?? iconA11yInfo?.tooltip;
  const defaultAriaLabel = props.tooltip ? [value, props.tooltip].join(', ') : iconAriaLabel ? [value, iconAriaLabel].join(', ') : undefined;
  const defaultTooltip = iconA11yInfo?.tooltip;
  const firstCellHasButton = props.buttonProps !== undefined || props.includeDropdown === true;
  // The gridcell div is only focusable when the first cell does not contain Button nor MenuButton
  // AND there is an icon or if it has a custom tooltip
  // because we want the icon and the text and the custom tooltip to be read by
  // assessible technology, NOVACOMMONS-12697
  const focusOnGridcell = !firstCellHasButton && (props.icon || props.tooltip);
  const defaultCloseButtonTooltip = props.includeDropdown ? localize('groupToken.closeIcon.tooltip.label') : localize('token.closeIcon.tooltip.label');
  const closeButtonTooltip = props.closeButtonProps?.tooltip ?? defaultCloseButtonTooltip;
  const defaultCloseButtonAriaLabel = [closeButtonTooltip, value].join(', ');
  if (props.asButton) {
    return /*#__PURE__*/_jsx(BaseButton, {
      ...props.buttonProps,
      disabled: props.disabled,
      text: value,
      icon: props.icon,
      variant: ButtonVariant.Small,
      className: cx(props.className, cn.tokenButton, isPopoverOpen && cn.dropdownOpen, props.disabled && cn.disabled),
      style: {
        ...tokens,
        ...props.style
      },
      onPress: props.onClick,
      tabIndex: props.tabIndex,
      testId: testId,
      ref: setTokenContainerRef,
      tooltip: props.tooltip ?? defaultTooltip,
      ariaLabel: props.ariaLabel ?? defaultAriaLabel,
      ariaDescribedBy: props.ariaDescribedBy,
      onKeyDown: props.onKeyDown,
      onFocus: props.onFocus,
      theme: () => tokenButtonTheme,
      "aria-haspopup": !!props.includeDropdown
    });
  }
  const firstCellContent = (() => {
    if (props.buttonProps) {
      return /*#__PURE__*/_jsx(BaseButton, {
        "aria-haspopup": !!props.includeDropdown,
        disabled: props.disabled,
        theme: () => gridBasedTokenButtonTheme,
        variant: ButtonVariant.LightSmall,
        focusable: focusableColumnIndex === 0,
        text: value,
        children: props.includeDropdown ? /*#__PURE__*/_jsx(Icon, {
          icon: ArrowSmallDownBtn,
          className: cx(cnBaseButton.iconInner, cn.downArrowIcon),
          tooltip: "",
          ariaHidden: true
        }) : null,
        icon: props.icon,
        tooltip: props.tooltip,
        ...props.buttonProps,
        className: cx(props.buttonProps.className, cn.gridBasedTokenMainButton),
        ref: e => {
          setRef(props.buttonProps?.ref, e);
          setRef(textRef, e);
        }
      });
    } else if (props.includeDropdown) {
      return /*#__PURE__*/_jsx(BaseMenuButton, {
        testId: postfixTestId(props.testId, 'menuButton'),
        ref: textRef,
        menuDockingElement: tokenContainerRef.current,
        variant: ButtonVariant.LightSmall,
        text: value,
        icon: props.icon,
        tooltip: props.tooltip,
        className: cn.gridBasedTokenMainButton,
        focusable: focusableColumnIndex === 0,
        buttonTheme: () => gridBasedTokenButtonTheme,
        sections: menuSections,
        disabled: props.disabled,
        onItemSelected: props.onItemSelected ?? (() => {}),
        onItemClosed: ([itemLocation]) => {
          const [, itemIndex] = itemLocation;
          props.onCloseItem?.(itemIndex);
        },
        onOpen: () => setMenuIsOpen(true),
        onDismiss: () => setMenuIsOpen(false),
        onMouseDown: () => setMenuIsOpen(true),
        onKeyDown: e => {
          if (['Enter', ' '].includes(e.key)) {
            setMenuIsOpen(true);
          }
        }
      });
    } else {
      return /*#__PURE__*/_jsx(Text, {
        testId: postfixTestId(props.testId, 'text'),
        className: cn.text,
        theme: () => ({
          '--text-fg': 'var(--token-fg)'
        }),
        wrapText: false,
        ref: focusOnGridcell ? undefined : textRef,
        tabIndex: focusOnGridcell ? undefined : focusableColumnIndex === 0 ? 0 : -1,
        children: value
      });
    }
  })();
  return /*#__PURE__*/_jsxs(TokenDesignContainer, {
    role: "row",
    spacing: "xs",
    className: cx(props.className, cn.token, props.disabled && cn.disabled, props.readOnly && cn.readOnly, (props.includeDropdown || props.buttonProps) && cn.includeButton, isPopoverOpen && cn.dropdownOpen),
    style: {
      ...tokens,
      ...props.style
    },
    onClick: e => {
      if (props.disabled) {
        return;
      }
      props.onClick?.();
      onClick(e);
    },
    "data-testid": testId,
    ref: setTokenContainerRef,
    tabIndex: -1,
    "aria-describedby": props.ariaDescribedBy,
    onKeyDown: e => {
      if (isPopoverOpen) {
        return;
      }
      props.onKeyDown?.(e);
      onKeyDown(e);
    },
    onBlur: onBlur,
    onFocus: e => {
      props.onFocus?.(e);
      onFocus(e);
    },
    dndProps: props.allowDrag ? {
      index: props.index ?? -1,
      id: props.id ?? props.value,
      dndPrefix,
      onDidDrop: props.onDidDrop
    } : undefined,
    children: [/*#__PURE__*/_jsxs(Flow, {
      role: "gridcell",
      spacing: "xs",
      className: cn.textWrapper,
      "aria-label": props.ariaLabel ?? defaultAriaLabel,
      "aria-disabled": props.disabled,
      title: props.tooltip ?? defaultTooltip,
      tabIndex: focusOnGridcell ? focusableColumnIndex === 0 ? 0 : -1 : undefined,
      ref: focusOnGridcell ? textRef : undefined,
      children: [props.icon && !firstCellHasButton ? /*#__PURE__*/_jsx(Icon, {
        ...props.icon,
        theme: () => ({
          '--icon-default-override-fg': 'var(--token-fg)'
        }),
        tooltip: "",
        ariaHidden: true,
        className: cx(cn.icon, props.icon.className)
      }) : null, firstCellContent]
    }), !props.readOnly ? /*#__PURE__*/_jsx("div", {
      role: "gridcell",
      "aria-disabled": props.disabled,
      className: cn.closeIconContainer,
      children: /*#__PURE__*/_jsx(Button, {
        variant: ButtonVariant.LightSmall,
        testId: postfixTestId(props.testId, 'closeIconFocusContainer'),
        tooltip: closeButtonTooltip,
        ariaLabel: props.closeButtonProps?.ariaLabel ?? defaultCloseButtonAriaLabel,
        ariaLabelledBy: props.closeButtonProps?.ariaLabelledBy,
        disabled: props.disabled,
        ref: closeRef,
        className: cn.gridBasedTokenIconButton,
        focusable: focusableColumnIndex === 1,
        onPress: () => {
          props.onClose?.();
          directSpeech?.notify(localize('tokenRemoved.aria', {
            label: value
          }));
        },
        icon: {
          icon: CloseTab,
          testId: postfixTestId(props.testId, 'close')
        },
        theme: () => gridBasedTokenButtonTheme
      })
    }) : null]
  });
});
export const getTokenDesignTokens = (dt, opts) => {
  let variantColor, variantBorderColor;
  switch (opts?.variant) {
    case 'primary':
      variantColor = dt.tokens['color-tag-background-1'];
      variantBorderColor = dt.tokens['color-tag-border-1'];
      break;
    case 'secondary':
      variantColor = dt.tokens['color-tag-background-2'];
      variantBorderColor = dt.tokens['color-tag-border-2'];
      break;
    default:
      variantColor = dt.tokens['color-tag-background-3'];
      variantBorderColor = dt.tokens['color-tag-border-3'];
  }
  const styleTokens = opts?.disabled ? {
    bg: dt.tokens['color-action-background-standard-disabled'],
    bd: dt.tokens['color-action-border-standard-disabled'],
    fg: dt.tokens['color-action-text-standard-disabled']
  } : {
    fg: dt.tokens['color-text-neutral'],
    bg: variantColor,
    bd: dt.themeVariant === 'high-contrast' ? variantBorderColor : 'transparent'
  };
  return {
    ...renderDesignTokens({
      ...styleTokens,
      'selected-bd': variantBorderColor,
      'selected-bd-width': dt.themeVariant === 'high-contrast' ? '2px' : '1px',
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'spacing-xxs': dt.tokens['space-base-xxs'],
      'font-size-s': dt.tokens['font-size-body-text-s'],
      'border-radius': dt.tokens['size-border-radius-xs'],
      'focus-outline': 0,
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'focus-outline': 'calc(var(--focus-width) * -1)'
      })
    }, 'token'),
    ...(!dt.enableNovaTokens && getTokenDesignLegacyTokens(dt, opts))
  };
};
export const getTokenDesignLegacyTokens = (dt, opts) => {
  // Unfortunately, Token uses its own set of design tokens.
  const darkOpacity = 0.4;
  const hcOpacity = 0.05;
  const lightOpacity = 0.1;
  let tokens;
  switch (opts?.variant) {
    case TokenVariant.Primary:
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          tokens = dt.reportTokens.statePrimary.normal;
      }
      switch (dt.themeVariant) {
        case 'dark':
          tokens = {
            fg: dt.c.white,
            bg: setAlpha(dt.c.p, darkOpacity),
            bd: dt.c.p
          };
          break;
        case 'high-contrast':
          tokens = {
            fg: dt.c.p3,
            bg: setAlpha(dt.c.p3, hcOpacity),
            bd: dt.c.p3
          };
          break;
        default:
          tokens = {
            fg: dt.c.p_1,
            bg: setAlpha(dt.c.p, lightOpacity),
            bd: dt.c.p
          };
          break;
      }
      break;
    case TokenVariant.Secondary:
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          tokens = dt.reportTokens.stateSecondary.normal;
      }
      switch (dt.themeVariant) {
        case 'dark':
          tokens = {
            fg: dt.c.white,
            bg: setAlpha(dt.c.e1, darkOpacity),
            bd: dt.c.e1
          };
          break;
        case 'high-contrast':
          tokens = {
            fg: dt.c.e2,
            bg: setAlpha(dt.c.e2, hcOpacity),
            bd: dt.c.e2
          };
          break;
        default:
          tokens = {
            fg: dt.c.e,
            bg: setAlpha(dt.c.e, lightOpacity),
            bd: dt.c.e
          };
          break;
      }
      break;
    default:
      switch (dt.baseReportThemeName) {
        case 'highcontrast2018':
        case 'highcontrast2017':
          tokens = dt.reportTokens.stateSecondary.normal;
      }
      switch (dt.themeVariant) {
        case 'dark':
          tokens = {
            fg: dt.c.white,
            bg: setAlpha(dt.c.n1, darkOpacity),
            bd: dt.c.n1
          };
          break;
        case 'high-contrast':
          tokens = {
            fg: dt.c.white,
            bg: setAlpha(dt.c.white, hcOpacity),
            bd: dt.c.white
          };
          break;
        default:
          tokens = {
            fg: dt.c.n_3,
            bg: setAlpha(dt.c.n1, lightOpacity),
            bd: dt.c.n1
          };
          break;
      }
  }
  return renderDesignTokens({
    ...tokens,
    'font-size-s': dt.reportTokens.font.size.s
  }, 'token');
};
const tokenButtonTheme = {
  '--button-fg': 'var(--token-fg)',
  '--button-bg': 'var(--token-bg)',
  '--button-bd': 'var(--token-bd)',
  '--button-filter': 'none',
  '--button-hover-fg': 'var(--token-fg)',
  '--button-hover-bg': 'var(--token-bg)',
  '--button-hover-bd': 'var(--token-selected-bd)',
  '--button-hover-filter': 'none',
  '--button-active-fg': 'var(--token-fg)',
  '--button-active-bg': 'var(--token-bg)',
  '--button-active-bd': 'var(--token-selected-bd)',
  '--button-active-filter': 'none'
};
const gridBasedTokenButtonTheme = {
  '--button-fg': 'var(--token-fg)',
  '--button-bg': 'transparent',
  '--button-bd': 'transparent',
  '--button-filter': 'none',
  '--button-hover-fg': 'var(--token-fg)',
  '--button-hover-bg': 'transparent',
  '--button-hover-bd': 'transparent',
  '--button-hover-filter': 'none',
  '--button-active-fg': 'var(--token-fg)',
  '--button-active-bg': 'transparent',
  '--button-active-bd': 'transparent',
  '--button-active-filter': 'none'
};
//# sourceMappingURL=TokenDesign.js.map